import {
  AssetType,
  Collateral,
  CollateralBasket,
  CollateralIdentifierType,
  CollateralStatus,
  Instrument,
  IssuerOrganisation,
  Market,
  ProductSector,
  RepoType
} from 'api'
import { isNotNil } from 'utils/predicate'

export function isZeroCouponBond(collateral: Collateral): boolean {
  return collateral.coupon === 0
}

export function isCollateral(instrument: Instrument): instrument is Collateral {
  return (instrument as Collateral).collateralIdentifierType !== undefined
}

export const isCollateralCorp = (collateral: Collateral | null) => {
  return collateral ? collateral.assetType === AssetType.CORP : false
}

export const isCollateralMort = (collateral: Collateral | null) => {
  return collateral ? collateral.assetType === AssetType.MORT_AGENCY || collateral.assetType === AssetType.MORT_NON_AGENCY : false
}

export const isOriginalCollateral = (collateral: Collateral, originalCollateral: Collateral | null) =>
  collateral.id === originalCollateral?.id

export const currencyIsDifferent = (collateral: Collateral, originalCollateral: Collateral | null) =>
  collateral.currency !== originalCollateral?.currency

export const collateralNotEnabled = (collateral: Collateral) => collateral.status !== CollateralStatus.ENABLED

export const isNotPartOfBasketCollateralIds = (
  parentBasketId: string,
  collateralId: string,
  getBasket: (basketId: string) => CollateralBasket | null
) => {
  const basket = getBasket(parentBasketId)?.collateralIds
  return !basket?.includes(collateralId)
}

export const isCompatibleWithOriginalRepoType = (
  getProductSector: (productSectorId: string) => ProductSector | null,
  productSectorId: string,
  originalRepoType: RepoType
) => {
  return getProductSector(productSectorId)?.supportedRepoTypes.includes(originalRepoType)
}

const isPending = (collateral: Collateral) => [CollateralStatus.PENDING, CollateralStatus.PENDING_FAILED].includes(collateral.status)
const isReadyForActivation = (collateral: Collateral) => CollateralStatus.READY_FOR_ACTIVATION === collateral.status
export const isActiveLike = (collateral: Collateral) => !isPending(collateral) && !isReadyForActivation(collateral)

export const getCollateralMarket = (collateral: Collateral) => {
  return collateral.collateralIdentifierType
    ? collateral.collateralIdentifierType === CollateralIdentifierType.ISIN
      ? Market.EUROPE
      : Market.US
    : undefined
}

export const collateralIsNotCompliant = (collateral: Collateral) => {
  if (
    !collateral.prospectus &&
    !collateral.rule144aIndicator &&
    !collateral.inEsmaFirds &&
    !collateral.eeaVenueEligible &&
    !collateral.ukVenueEligible &&
    !collateral.complianceOverride
  ) {
    return true
  } else {
    return false
  }
}

export const ultimateParentOrgSanctionScreened = (collateral: Collateral, issuerOrganisations: IssuerOrganisation[]) => {
  const collateralIssuerOrganisation = issuerOrganisations.find(
    issuerOrganisation => issuerOrganisation.issuerOrganisationId?.toString() === collateral.issuerOrgId
  )
  const ultimateParentOrg = issuerOrganisations.find(
    issuerOrganisation => issuerOrganisation.issuerOrganisationId === collateralIssuerOrganisation?.ultimateParentIssuerOrganisationId
  )
  const sanctionScreened = ultimateParentOrg?.sanctionScreened

  return isNotNil(ultimateParentOrg) ? sanctionScreened : true
}

export const issuerOrganisationComplianceSuspended = (collateral: Collateral, issuerOrganisations: IssuerOrganisation[]) => {
  const collateralIssuerOrganisation = issuerOrganisations.find(
    issuerOrganisation => issuerOrganisation.issuerOrganisationId?.toString() === collateral.issuerOrgId
  )
  const complianceSuspended = collateralIssuerOrganisation?.complianceSuspended

  return isNotNil(collateralIssuerOrganisation) ? complianceSuspended : false
}
